import { K2Lookup } from "./K2lookups"
import { K2RailType } from "./K2Types"

// The following schema are copied from https://k2-calculator-api.developer.azure-api.net/ with some modifications. They are used for parsing to create input for K2 mounting.
export type K2SchemaProperties = Partial<{
  type: string
  description: string
  enum: (string | number)[]
  default: string | number | boolean
  items: any
  $ref: string
}>

export interface K2Schema {
  type?: string
  description?: string
  properties: Record<string, K2SchemaProperties>
  required: string[]
}

export const K2CrossRailSchema = {
    "properties": {
        "modulePerformance": {
            "type": "number",
            "description": "The maximum power output of a single solar module in watts (W)."
        },
        "region": {
            "type": "string",
            "enum": [
                "US"
            ],
            "default": "US",
            "description": "Region used to validate available parts according to the lookups endpoint configuration; affects which parts are returned. Currently only US is supported."
        },
        "unitSystem": {
            "type": "string",
            "enum": [
                "imperial"
            ],
            "default": "imperial",
            "description": "Defines the unit system to be used across the entire project. Defaults to 'imperial', which is currently the only supported unit system."
        },
        "enforceBusinessRules": {
            "type": "boolean",
            "default": true,
            "description": "When `true`, enforces strict K2 business-rule validations (as defined in the lookups endpoint) for all fields: it checks that combinations like `railType`+`railLength`, finishes (`railFinish`/`clampFinish`), and parts (rails, connectors, clamps, attachments, fasteners) are compatible with your configuration. When `false`, only required fields are checked, and you may submit any parts and other fields without business-rule validation. For more information on the validation rules, refer to the lookups endpoint."
        },
        "customModuleUnit": {
            "type": "string",
            "enum": [
                "inches",
                "meters"
            ],
            "default": "inches",
            "description": "The unit of measurement used for the custom module dimensions (length, width). Defaults to 'inches'. Possible values include 'inches' and 'meters'. Note: the height will always be calculated in millimeters (mm), regardless of the selected unit."
        },
        "customModuleHeight": {
            "type": "number",
            "description": "The height of a custom solar module. This value will always be interpreted and calculated in millimeters (mm), regardless of the unit specified in customModuleUnit."
        },
        "customModuleLength": {
            "type": "number",
            "description": "The length of a custom solar module in the specified custom unit (inches or meters)."
        },
        "customModuleWidth": {
            "type": "number",
            "description": "The width of a custom solar module in the specified custom unit (inches or meters)."
        },
        "roofs": {
            "type": "array",
            "items": {
                "$ref": "#/definitions/ProjectRoofType"
            },
            "description": "An array containing information about the roofs included in the project."
        },
        "mountingOption": {
            "type": "string",
            "enum": [
                "rafter",
                "deck"
            ],
            "default": "rafter",
            "description": "Specifies whether parts attach to rafters or deck. Defaults to 'rafter' if omitted. This setting determines which attachment and fastener options are available—see the lookups endpoint for the exact options supported for each mountingOption value."
        },
        "rail": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "description": "The rail part used in the project, represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported rail options and configurations, consult the lookups endpoint. If omitted, we will automatically supply a part compatible with your configuration."
        },
        "accessories": {
            "type": "array",
            "items": {
                "$ref": "#/definitions/ProjectBOMItemType"
            },
            "description": "An array of accessory components used in the project, each represented by an object that must include a valid part number that exists in our system."
        },
        "attachments": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "enum": Array.from(new Set(Object.values(K2Lookup.data.US.imperial.k2OnRoof.fields.attachments.mapping).flatMap(obj => Object.values(obj)).flat())),

            "description": "The attachment part for this project (required), represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported attachment options and configurations, consult the lookups endpoint."
        },
        "fastener": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "enum": Array.from(new Set(Object.values(K2Lookup.data.US.imperial.k2OnRoof.fields.fasteners.mapping).flatMap(roofCovering => 
                Object.values(roofCovering).flatMap(mountingOption => 
                    Object.values(mountingOption).flat()
                )
            ).flat())),
            "description": "Optional. The fastener part used in the project, represented by an object. Depends on the selected `attachments`. If `enforceBusinessRules` is true, `partNumber` will be validated against our inventory; consult the lookup endpoint to see which fasteners are available for your configuration."
        },
        "connectors": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "description": "The connector part used in the project, represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported connector options and configurations, consult the lookups endpoint. If omitted, we will automatically supply a part compatible with your configuration."
        },
        "everestLugs": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "description": "The Everest lug part used in the project (if applicable), represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported Everest lug options and configurations, consult the lookups endpoint. If omitted, we will automatically supply a part compatible with your configuration."
        },
        "midClamp": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "description": "The mid clamp part used in the project, represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported mid clamp options and configurations, consult the lookups endpoint. If omitted, we will automatically supply a part compatible with your configuration."
        },
        "railType": {
            "type": "string",
            "enum": [
                "44-X",
                "44-X-Max",
                "48-X",
                "48-XL"
            ],
            "description": "The type of rail component used in the project. Available options include '44-X', '44-X-Max', '48-X', and '48-XL'."
        },
        "railLength": {
                "type": "integer",
                "enum": [
                    86,
                    172,
                    185
                ],
                "description": "The length of the rail component in inches. You may choose from 86, 172, or 185, and consult the lookups endpoint for detailed compatibility with each rail type."
        },
        "railFinish": {
            "type": "string",
            "enum": [
                "Mill",
                "Dark"
            ],
            "description": "The finish applied to the rail component (Mill or Dark). Check the lookups endpoint to see which finishes are available for your configuration."
        },
        "clampFinish": {
            "type": "string",
            "enum": [
                "Mill",
                "Dark"
            ],
            "description": "The finish applied to the clamp component (Mill or Dark). Check the lookups endpoint to see which finishes are available for your configuration."
        },
        "endClamp": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "type": "string",
            "enum": ['4000145', '4000050-B', '4000135'],
            "description": "The end clamp part used in the project, represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported end clamp options and configurations, consult the lookups endpoint. If omitted, we will automatically supply a part compatible with your configuration."
        },
        "attachmentSpacing": {
                    "type": "integer",
                    "enum": Array.from({ length: 96 - 16 + 1}, (_, i) => 16 + i), 
                    "default": 48,
                    "description": "The spacing between attachment points on the roof in inches. Common values are 48 and 72 inches. Maximum allowed is 96 inches."
        },
        "customParts": {
            "type": "array",
            "items": {
                "$ref": "#/definitions/ProjectBOMCustomField"
            },
            "description": "A collection of custom parts with specific part numbers, descriptions, quantities, and pricing tailored to the project. These parts do not need to exist in our system and can be defined freely by the user."
        },
        "engineering": {
            "$ref": "#/definitions/EngineeringInputs",
            "description": "A reference to the engineering data object containing project-specific calculations."
        }
    },
    "required": [
        "modulePerformance",
        "customModuleUnit",
        "customModuleHeight",
        "customModuleLength",
        "customModuleWidth",
        "roofs",
        "attachmentSpacing",
        "attachments",
        "railType",
        "railLength",
        "railFinish",
        "clampFinish"
    ],
} satisfies K2Schema

export const K2CrossrailEngineeringSchema = {
    "type": "object",
    "description": "Optional inputs for performing CrossRail structural calculations. If provided, these fields (panelLength, tiltAngle, roofShape, windSpeed, etc.) will be used to compute engineering results; if omitted, no engineering calculations are performed and no engineering output is returned.",
    "properties": {
        "panelLength": {
            "type": "number",
            "description": "The length of the solar panel in inches."
        },
        "tiltAngle": {
            "type": "string",
            "description": "The tilt angle zone of the solar array. This value influences factors like solar exposure and snow accumulation.",
            "enum": [
                "7-20",
                "20-27",
                "27-45",
                "3-10",
                "10-30"
            ]
        },
        "roofShape": {
            "type": "string",
            "description": "The type of roof the solar array will be mounted on.",
            "default": 'monoslope',
            "enum": [
                "gable",
                "monoslope"
            ]
        },
        "windSpeed": {
            "type": "number",
            "description": "The maximum wind speed in miles per hour (mph) expected at the project site."
        },
        
        "designSnowLoad": {
            "type": "number",
            "description": "The design snow load in pounds per square foot (psf) used for structural calculations. This value considers the weight of snow accumulation on the solar array."
        },
        "buildingHeight": {
            "type": "string",
            "default": "0-15",
            "description": "The height or height range of the building where the solar array will be installed, specified in feet. This value is used for structural calculations.",
            "enum": [
                "0-15",
                "20",
                "25",
                "30",
                "35",
                "40",
                "45",
                "50"
            ]
        },
        "designCode": {
            "type": "string",
            "enum": [
                "ASCE 7-16",
                "ASCE 7-22"
            ],
            "default": "ASCE 7-16",
            "description": "Specifies the design code applied to the project, indicating the engineering standards used. Two codes are available: 'ASCE 7-16' and 'ASCE 7-22'. If no code is selected, the default code 'ASCE 7-16' will be applied."
        },
        "exposureCategory": {
            "type": "string",
            "description": "The exposure category of the project site, which considers factors like surrounding terrain and buildings. This value is used for structural calculations.",
            "default": "C",
            "enum": [
                "B",
                "C",
                "D"
            ]
        },
    },
    "required": [
        "panelLength",
        "tiltAngle",
        "roofShape",
        "windSpeed",
        "exposureCategory",
        "designSnowLoad",
        "railType",
        "buildingHeight",
        "attachmentSpacing"
    ],
} satisfies K2Schema

export const K2SimpleTiltSchema = {
    "properties": {
        "modulePerformance": {
            "type": "number",
            "description": "The maximum power output of a single solar module in watts (W)."
        },
        "region": {
            "type": "string",
            "enum": [
                "US"
            ],
            "default": "US",
            "description": "Region used to validate available parts according to the lookups endpoint configuration; affects which parts are returned. Currently only US is supported."
        },
        "unitSystem": {
            "type": "string",
            "enum": [
                "imperial"
            ],
            "default": "imperial",
            "description": "Defines the unit system to be used across the entire project. Defaults to 'imperial', which is currently the only supported unit system."
        },
        "enforceBusinessRules": {
            "type": "boolean",
            "default": true,
            "description": "When `true`, enforces strict K2 business-rule validations (as defined in the lookups endpoint) for all fields: it checks that combinations like `railType`+`railLength`, finishes (`railFinish`/`clampFinish`), and parts (rails, connectors, clamps, attachments, fasteners) are compatible with your configuration. When `false`, only required fields are checked, and you may submit any parts and other fields without business-rule validation. For more information on the validation rules, refer to the lookups endpoint."
        },
        "customModuleUnit": {
            "type": "string",
            "enum": [
                "inches",
                "meters"
            ],
            "default": "inches",
            "description": "The unit of measurement used for the custom module dimensions (length, width). Defaults to 'inches'. Possible values include 'inches' and 'meters'. Note: the height will always be calculated in millimeters (mm), regardless of the selected unit."
        },
        "customModuleHeight": {
            "type": "number",
            "description": "The height of a custom solar module. This value will always be interpreted and calculated in millimeters (mm), regardless of the unit specified in customModuleUnit."
        },
        "customModuleLength": {
            "type": "number",
            "description": "The length of a custom solar module in the specified custom unit (inches or meters)."
        },
        "customModuleWidth": {
            "type": "number",
            "description": "The width of a custom solar module in the specified custom unit (inches or meters)."
        },
        "roofs": {
            "type": "array",
            "items": {
                "$ref": "#/definitions/ProjectRoofType-SimpleTilt"
            },
            "description": "An array containing information about the roofs included in the project."
        },
        "attachmentSpacing": {
                    "type": "integer",
                    "enum": Array.from({ length: 96 - 16 + 1}, (_, i) => 16 + i), 
                    "default": 48,
                    "description": "The spacing between attachment points on the roof in inches. Common values are 48 and 72 inches. Maximum allowed is 96 inches."
        },
        "rail": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "description": "The rail part used in the project, represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported rail options and configurations, consult the lookups endpoint. If omitted, we will automatically supply a part compatible with your configuration."
        },
        "endClamp": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "type": "string",
            "enum": ['4000145', '4000050-B', '4000135'],
            "description": "The end clamp part used in the project, represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported end clamp options and configurations, consult the lookups endpoint. If omitted, we will automatically supply a part compatible with your configuration."
        },
        "accessories": {
            "type": "array",
            "items": {
                "$ref": "#/definitions/ProjectBOMItemType"
            },
            "description": "An array of accessory components used in the project, each represented by an object. When enforceBusinessRules is true, each item must include a valid partNumber that exists in our system."
        },
        "attachmentAccessories": {
            "type": "array",
            "items": {
                "$ref": "#/definitions/ProjectBOMItemType"
            },
            "description": "Accessory parts used to support attachments. Each item is an object and, when enforceBusinessRules is true, must include a valid partNumber that exists in our system."
        },
        "attachments": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "enum": Array.from(new Set(Object.values(K2Lookup.data.US.imperial.k2SimpleTilt.fields.attachments.mapping).flatMap(obj => Object.values(obj)).flat())),
            "description": "The attachment part for this project (required), represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported attachment options and configurations, consult the lookups endpoint."
        },
        "fastener": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "description": "Optional. The fastener part used in the project, represented by an object. Depends on the selected `attachments`. If `enforceBusinessRules` is true, `partNumber` will be validated against our inventory; consult the lookup endpoint to see which fasteners are available for your configuration."
        },
        "connectors": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "description": "The connector part used in the project, represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported connector options and configurations, consult the lookups endpoint. If omitted, we will automatically supply a part compatible with your configuration."
        },
        "everestLugs": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "description": "The Everest lug part used in the project (if applicable), represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported Everest lug options and configurations, consult the lookups endpoint. If omitted, we will automatically supply a part compatible with your configuration."
        },
        "midClamp": {
            "$ref": "#/definitions/ProjectBOMItemType",
            "description": "The mid clamp part used in the project, represented by an object. When `enforceBusinessRules` is true, this must include a valid partNumber in our system; to see all supported mid clamp options and configurations, consult the lookups endpoint. If omitted, we will automatically supply a part compatible with your configuration."
        },
        "railType": {
            "type": "string",
            "enum": [
                "44-X",
                "44-X-Max",
                "48-X",
                "48-XL"
            ],
            "description": "The type of rail component used in the project. Available options include '44-X', '44-X-Max', '48-X', and '48-XL'."
        },
        "railLength": {
            "type": "integer",
            "enum": [
                86,
                172,
                185
            ],
            "description": "The length of the rail component in inches. You may choose from 86, 172, or 185, and consult the lookups endpoint for detailed compatibility with each rail type."
        },
        "railFinish": {
            "type": "string",
            "enum": [
                "Mill",
                "Dark"
            ],
            "description": "The finish applied to the rail component (Mill or Dark). Check the lookups endpoint to see which finishes are available for your configuration."
        },
        "clampFinish": {
            "type": "string",
            "enum": [
                "Mill",
                "Dark"
            ],
            "description": "The finish applied to the clamp component (Mill or Dark). Check the lookups endpoint to see which finishes are available for your configuration."
        },
        "customParts": {
            "type": "array",
            "items": {
                "$ref": "#/definitions/ProjectBOMCustomField"
            },
            "description": "A collection of custom parts with specific part numbers, descriptions, quantities, and pricing tailored to the project. These parts do not need to exist in our system and can be defined freely by the user."
        },
        "engineering": {
            "$ref": "#/definitions/EngineeringInputs-SimpleTilt",
            "description": "A reference to the engineering data object containing project-specific calculations."
        }
    },
    "required": [
        "modulePerformance",
        "customModuleUnit",
        "customModuleHeight",
        "customModuleLength",
        "customModuleWidth",
        "roofs",
        "attachmentSpacing",
        "attachments",
        "railType",
        "railLength",
        "railFinish",
        "clampFinish",
        "engineering"
    ],
} satisfies K2Schema

export const K2SimpleTiltEngineeringSchema = {
    "type": "object",
    "description": "Required inputs for performing SimpleTilt structural calculations. These are required for the SimpleTilt calculator.",
    "properties": {
        "designCode": {
            "type": "string",
            "enum": [
                "ASCE 7-16"
            ],
            "default": "ASCE 7-16",
            "description": "Specifies the design code applied to the project, indicating the engineering standards used. One code is available: 'ASCE 7-16'. If no code is selected, the default code 'ASCE 7-16' will be applied."
        },
        "tiltAngle": {
            "type": "string",
            "description": "The tilt angle zone of the solar array. This value influences factors like solar exposure and snow accumulation.  Flat roofs only support a tilt angle of 0-5.",
            "enum": [
                "0-5"
            ],
            "default": "0-5"
        },
        "roofShape": {
            "type": "string",
            "description": "The type of roof the solar array will be mounted on.",
            "enum": [
                "flat"
            ],
            "default": "flat"
        },
        "windSpeed": {
            "type": "number",
            "description": "The maximum wind speed in miles per hour (mph) expected at the project site."
        },
        "exposureCategory": {
            "type": "string",
            "description": "The exposure category of the project site, which considers factors like surrounding terrain and buildings. This value is used for structural calculations.",
            "enum": [
                "B",
                "C",
                "D"
            ]
        },
        "designSnowLoad": {
            "type": "number",
            "description": "The design snow load in pounds per square foot (psf) used for structural calculations. This value considers the weight of snow accumulation on the solar array."
        },
        "buildingHeight": {
            "type": "string",
            "description": "The height or height range of the building where the solar array will be installed, specified in feet. This value is used for structural calculations.",
            "enum": [
                "0-15",
                "20",
                "25",
                "30",
                "35",
                "40",
                "45",
                "50"
            ]
        },
        "riskCategory": {
            "type": "number",
            "description": "The risk category of the building where the solar array will be installed. This value is used for structural calculations.",
            "enum": [
                1,
                2,
                3,
                4
            ],
            "default": 2
        },
        "siteElevation": {
            "type": "number",
            "description": "The elevation of the site where the solar array will be installed, specified in feet. This value is used for structural calculations.",
            "default": 0
        },
        "reactionForces": {
            "type": "boolean",
            "description": "Calculate forces at attachment points (uplift: negative vertical force, downforce: positive vertical force, shear: horizontal force) to determine mounting requirements. Default value is `false`.",
            "default": false
        }
    },
    "required": [
        "buildingHeight",
        "designSnowLoad",
        "windSpeed",
        "exposureCategory"
    ]
} satisfies K2Schema

export const K2SchemaMap: Record<K2RailType, K2Schema> = {
    crossrail: K2CrossRailSchema,
    simpleTilt: K2SimpleTiltSchema,
    // to be extended
}